import { useState, useCallback } from "react";
import { TRIGGER_CONSTANTS, VALIDATION_MESSAGES } from "../constants/triggerConstants";
import { useTriggers } from "../context/TriggersContext";

export const useTriggerValidation = (originalTriggerNameRef = null, isNewTrigger = true) => {
  const { getExistingTriggerNames } = useTriggers();

  const [errors, setErrors] = useState({
    name: "",
    description: "",
  });

  const [formState, setFormState] = useState({
    name: "default",
    description: "default",
  });

  const validateName = useCallback((name) => {
    if (!name.trim()) {
      setErrors((prev) => ({ ...prev, name: VALIDATION_MESSAGES.NAME_REQUIRED }));
      setFormState((prev) => ({ ...prev, name: "error" }));
      return false;
    }

    if (!TRIGGER_CONSTANTS.VALID_NAME_REGEX.test(name)) {
      setErrors((prev) => ({
        ...prev,
        name: VALIDATION_MESSAGES.NAME_INVALID_CHARACTERS,
      }));
      setFormState((prev) => ({ ...prev, name: "error" }));
      return false;
    }

    let namesToCheck = getExistingTriggerNames();
    
    if (!isNewTrigger && originalTriggerNameRef?.current) {
      const originalName = originalTriggerNameRef.current.trim();
      namesToCheck = namesToCheck.filter(existingName => existingName !== originalName);
    }
    
    if (namesToCheck.includes(name.trim())) {
      setErrors((prev) => ({
        ...prev,
        name: VALIDATION_MESSAGES.NAME_ALREADY_EXISTS,
      }));
      setFormState((prev) => ({ ...prev, name: "error" }));
      return false;
    }

    setErrors((prev) => ({ ...prev, name: "" }));
    setFormState((prev) => ({ ...prev, name: "default" }));
    return true;
  }, [getExistingTriggerNames, isNewTrigger, originalTriggerNameRef]);

  const validateDescription = useCallback((description) => {
    if (description && description.length > TRIGGER_CONSTANTS.MAX_DESCRIPTION_LENGTH) {
      setErrors((prev) => ({
        ...prev,
        description: `Description must be under ${TRIGGER_CONSTANTS.MAX_DESCRIPTION_LENGTH} characters`,
      }));
      setFormState((prev) => ({ ...prev, description: "error" }));
      return false;
    }

    setErrors((prev) => ({ ...prev, description: "" }));
    setFormState((prev) => ({ ...prev, description: "default" }));
    return true;
  }, []);

  const validateAll = useCallback((formData) => {
    const nameValid = validateName(formData.name);
    const descriptionValid = validateDescription(formData.description);

    return nameValid && descriptionValid;
  }, [validateName, validateDescription]);

  const clearErrors = useCallback(() => {
    setErrors({ name: "", description: "" });
    setFormState({ name: "default", description: "default" });
  }, []);

  const hasValidationErrors = useCallback(() => {
    return errors.name !== "" || errors.description !== "";
  }, [errors]);

  return {
    errors,
    formState,
    validateName,
    validateDescription,
    validateAll,
    clearErrors,
    hasValidationErrors,
  };
}; 